import { useChannelAvatar } from "@/lib/hooks/conversations/use-channel-avatar.tsx";
import { useChannelPriority } from "@/lib/hooks/conversations/use-channel-priority.ts";
import { useIsChannelOnline } from "@/lib/hooks/conversations/use-is-channel-online.ts";
import { cn } from "@/lib/utils.ts";
import type { GroupChannel } from "@sendbird/chat/groupChannel";
import { TypingIndicatorText } from "@sendbird/uikit-react/GroupChannel/components/TypingIndicator";
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { Message } from "postcss";
import { useMemo } from "react";
import { RoleBadge } from "../users/role-badge";
import { SignalIcon } from "@heroicons/react/24/outline";
import { Label } from "@/components/ui/label";
import { shouldShowCheckinBadge } from "@/lib/utils/check-in-badge";

interface ChannelPreviewProps {
  channel: GroupChannel;
  currentChannelUrl: string | null;
  showRoleBadge?: boolean;
  shouldEverShowCheckinBadge?: boolean;
}

interface UserMetadata {
  role?: "MENTEE" | "MENTOR" | "admin";
  isSupervisor?: boolean;
  check_in_frequency?: string;
}

export function ChannelPreview({
  channel,
  currentChannelUrl,
  showRoleBadge = false,
  shouldEverShowCheckinBadge = false,
}: ChannelPreviewProps) {
  const store = useSendbirdStateContext();

  const { data: priority } = useChannelPriority(channel);
  const { lastMessage, unreadMessageCount } = channel;

  const channelTitle = useMemo(() => {
    // if (channelName) {
    //   return channelName.replace(user?.name || "", "");
    // }

    const membersNamesExcludingMe = channel.members
      .filter((member) => member.userId !== store.config.userId)
      .map((member) => member.nickname || member.userId)
      .join(", ");

    return membersNamesExcludingMe.length < 30
      ? membersNamesExcludingMe.length === 0
        ? "(No members)"
        : membersNamesExcludingMe
      : `${membersNamesExcludingMe.slice(0, 30)}...`;
  }, [store.config.userId, channel]);

  const isMenteeConversation = useMemo(() => {
    const otherMember = channel.members.find(
      (member) => member.userId !== store.config.userId,
    );

    const metadata = otherMember?.metaData as UserMetadata;

    return metadata?.role === "MENTEE";
  }, [channel, store.config.userId]);

  const showCheckinBadge = useMemo(() => {
    return (
      shouldEverShowCheckinBadge &&
      shouldShowCheckinBadge(channel, store.config.userId)
    );
  }, [channel, store.config.userId, shouldEverShowCheckinBadge]);

  const lastMessageText = useMemo(() => {
    if (!lastMessage) return null;

    // this is a hack to get around the fact that the SDK typings are incorrect
    const lastMessageObj: Message & {
      message: string;
      url: string;
    } = lastMessage as any;

    if (
      lastMessageObj.messageType === "admin" ||
      lastMessageObj.messageType === "user"
    ) {
      return lastMessageObj.message.length < 30
        ? lastMessageObj.message
        : `${lastMessageObj.message.slice(0, 30)}...`;
    }

    if (lastMessageObj.messageType === "file") {
      return lastMessageObj.url.length < 30
        ? lastMessageObj.url
        : `${lastMessageObj.url.slice(0, 30)}...`;
    }

    return null;
  }, [lastMessage]);

  const isOnline = useIsChannelOnline(channel, store.config.userId);

  const isNewTeen = useMemo(() => {
    const createdAt = new Date(channel.createdAt).getTime();
    const now = Date.now();
    const diffTime = now - createdAt; // Direct subtraction is fine
    const diffDays = diffTime / (1000 * 60 * 60 * 24); // No need for Math.abs() unless you expect negative times

    return diffDays < 7; // Less than 7 full days
  }, [channel.createdAt]);


  const channelAvatar = useChannelAvatar(
    channel,
    store.config.userId,
    !!priority,
    isOnline,
    isNewTeen,
  );

  const timestampText = useMemo(() => {
    if (lastMessage) {
      return new Date(lastMessage.createdAt).toLocaleString("en-US", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      });
    }
  }, [lastMessage]);

  return (
    <div
      className={cn(
        "h-18 flex w-full cursor-pointer flex-row items-center gap-2 border-b border-zinc-300 py-4 pl-1 pr-2",
        {
          "bg-indigo-100": currentChannelUrl === channel.url,
        },
      )}
    >
      <div
        className={cn("h-2 w-2 rounded-full", {
          "bg-blue-700": unreadMessageCount > 0,
        })}
      />

      {channelAvatar}

      <div className="flex flex-1 flex-col gap-1">
        <div className="flex flex-row items-center justify-between">
          <div className="mr-2 flex min-w-0 flex-1 items-center">
            <div className="flex min-w-0 flex-1 items-center">
              <span className="truncate text-base font-bold text-black">
                {channelTitle}
              </span>
              {showCheckinBadge && (
                <Label className="ml-2 whitespace-nowrap rounded-xs bg-[#FFDAD4] px-2 py-1 text-xs">
                  Check-in
                </Label>
              )}
            </div>
            {showRoleBadge && (
              <RoleBadge
                role={isMenteeConversation ? "mentee" : "mentor"}
                size="sm"
              />
            )}
          </div>
          <span className="shrink-0 text-xs font-normal text-zinc-400">
            {timestampText}
          </span>
        </div>

        <span className="text-sm font-normal text-stone-500">
          {channel.isTyping ? (
            <TypingIndicatorText members={channel.getTypingUsers()} />
          ) : (
            lastMessageText || "No messages"
          )}
        </span>

        {isNewTeen && (
          <span className="flex flex-row items-center gap-1 text-xs font-normal text-[#138A00]">
            <SignalIcon className="h-4 w-4" />
            New Teen
          </span>
        )}
      </div>
    </div>
  );
}
